if (!customElements.get('lus-proof-gallery')) {
  customElements.define('lus-proof-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.mounted === 'true') return;
      this.dataset.mounted = 'true';
      this.slides = [...this.querySelectorAll('[data-lus-proof-slide]')];
      this.tabs = [...this.querySelectorAll('[data-lus-proof-tab]')];
      this.descriptors = [...this.querySelectorAll('[data-lus-proof-descriptor]')];
      this.previous = this.querySelector('[data-lus-proof-previous]');
      this.next = this.querySelector('[data-lus-proof-next]');
      this.stage = this.querySelector('[data-lus-proof-stage]');
      this.index = this.index ?? 0;
      this.listeners = new AbortController();
      const listen = (target, type, handler) => target?.addEventListener(type, handler, { signal: this.listeners.signal });
      this.tabs.forEach((tab) => listen(tab, 'click', () => this.navigate(Number(tab.dataset.index))));
      listen(this.previous, 'click', () => this.navigate(this.index - 1));
      listen(this.next, 'click', () => this.navigate(this.index + 1));
      listen(this.querySelector('[role="tablist"]'), 'keydown', (event) => this.onKeydown(event));
      listen(this.stage, 'pointerdown', (event) => this.onPointerDown(event));
      listen(this.stage, 'pointerup', (event) => this.onPointerUp(event));
      listen(window, 'pointerup', () => { if (this.interacting || this.pointerStart) this.endInteraction(); });
      listen(window, 'pointercancel', () => { if (this.interacting || this.pointerStart) this.endInteraction(); });
      this.show(this.index);
      this.autoplay = this.hasAttribute('data-lus-proof-autoplay');
      if (!this.autoplay) return;
      this.toggle = this.querySelector('[data-lus-proof-toggle]');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = this.motion.matches || (this.userPaused ?? false);
      this.inView = false;
      this.interacting = false;
      this.resumeFocus = null;
      listen(this.toggle, 'click', () => {
        this.userPaused = !this.userPaused;
        this.resumeFocus = !this.userPaused && document.activeElement === this.toggle ? this.toggle : null;
        this.syncAutoplay();
      });
      listen(this, 'pointerdown', () => { this.interacting = true; this.syncAutoplay(); });
      listen(this, 'wheel', () => this.pause());
      listen(this, 'focusin', (event) => {
        if (event.target !== this.resumeFocus) this.resumeFocus = null;
        this.syncAutoplay();
      });
      listen(this, 'focusout', (event) => {
        if (event.target === this.resumeFocus) this.resumeFocus = null;
        this.syncAutoplay(event.relatedTarget);
      });
      listen(document, 'visibilitychange', () => {
        this.interacting = false;
        this.pointerStart = null;
        this.syncAutoplay();
      });
      listen(window, 'resize', () => this.syncAutoplay());
      listen(this.motion, 'change', () => {
        if (this.motion.matches) this.userPaused = true;
        this.syncAutoplay();
      });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.inView = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5);
          this.syncAutoplay();
        }, { threshold: [0, 0.5] });
        this.observer.observe(this.stage || this);
      }
      this.syncAutoplay();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.timer = null;
      this.listeners?.abort();
      this.observer?.disconnect();
      this.observer = null;
      this.pointerStart = null;
      this.dataset.mounted = 'false';
    }

    pause() {
      if (!this.autoplay) return;
      this.userPaused = true;
      this.resumeFocus = null;
      this.syncAutoplay();
    }

    navigate(index, focusTab = false) {
      this.pause();
      this.show(index, focusTab);
    }

    endInteraction() {
      this.pointerStart = null;
      this.interacting = false;
      this.syncAutoplay();
    }

    syncAutoplay(focused = document.activeElement) {
      clearTimeout(this.timer);
      this.timer = null;
      if (!this.autoplay) return;
      const available = this.slides.length > 1;
      if (this.toggle) {
        this.toggle.hidden = !available;
        const label = this.userPaused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel;
        this.toggle.setAttribute('aria-label', label);
        this.toggle.setAttribute('title', label);
        this.toggle.querySelector('[data-lus-proof-play]').toggleAttribute('hidden', !this.userPaused);
        this.toggle.querySelector('[data-lus-proof-pause]').toggleAttribute('hidden', this.userPaused);
      }
      this.stage?.setAttribute('aria-live', this.userPaused ? 'polite' : 'off');
      this.querySelector('.lus-proof__descriptors')?.setAttribute('aria-live', this.userPaused ? 'polite' : 'off');
      const focusInside = focused && this.contains(focused) && focused !== this.resumeFocus;
      if (!available || this.userPaused || !this.inView || document.hidden || this.interacting || focusInside || !this.isConnected) return;
      this.timer = setTimeout(() => {
        this.timer = null;
        this.show((this.index + 1) % this.slides.length);
        this.syncAutoplay();
      }, 2000);
    }

    show(nextIndex, focusTab = false) {
      if (!this.slides.length) {
        if (this.previous) this.previous.disabled = true;
        if (this.next) this.next.disabled = true;
        return;
      }
      const index = Math.max(0, Math.min(this.slides.length - 1, nextIndex));
      this.index = index;

      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        if (active) slide.removeAttribute('inert');
        else slide.setAttribute('inert', '');
      });

      this.tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === index;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });

      this.descriptors.forEach((descriptor, descriptorIndex) => {
        const active = descriptorIndex === index;
        descriptor.classList.toggle('is-active', active);
        descriptor.setAttribute('aria-hidden', String(!active));
      });

      if (this.previous) this.previous.disabled = this.slides.length < 2 || index === 0;
      if (this.next) this.next.disabled = this.slides.length < 2 || index === this.slides.length - 1;
      if (focusTab) this.tabs[index]?.focus();
    }

    onKeydown(event) {
      const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
      if (!keys.includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home') this.navigate(0, true);
      else if (event.key === 'End') this.navigate(this.slides.length - 1, true);
      else this.navigate(this.index + (event.key === 'ArrowRight' ? 1 : -1), true);
    }

    onPointerDown(event) {
      if (!this.autoplay && event.pointerType === 'mouse') return;
      if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
      this.pause();
      this.pointerStart = { x: event.clientX, y: event.clientY };
    }

    onPointerUp(event) {
      if (!this.pointerStart) return;
      const deltaX = event.clientX - this.pointerStart.x;
      const deltaY = event.clientY - this.pointerStart.y;
      this.pointerStart = null;
      if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      this.navigate(this.index + (deltaX < 0 ? 1 : -1));
    }
  });
}
